import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHero from '../components/page/PageHero';

const { FiCalendar, FiUser, FiArrowRight, FiSearch } = FiIcons;

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const blogPosts = [
    {
      id: 10,
      title: 'Porsche Cayenne Suspension: What Every Dallas Owner Needs to Know',
      excerpt: 'Real talk about Porsche Cayenne suspension issues. From air compressor failures to PASM problems, we fix them daily. Learn what breaks on 955/957 Cayennes and what it costs to repair in Dallas.',
      category: 'Service',
      author: 'Paradise-Performance',
      date: '2025-01-15',
      image: '/assets/assets/images/business-shop-sign.webp',
      slug: 'porsche-cayenne-suspension-dallas',
    },
    {
      id: 8,
      title: 'Porsche PSM Failure: A Mechanic\'s Guide to This Common Nightmare',
      excerpt: 'PSM failure in a Porsche indicates that the Porsche Stability Management system has malfunctioned and can no longer provide electronic stability control. Learn about warning lights, causes, and repair solutions.',
      category: 'Service',
      author: 'Paradise-Performance Technicians',
      date: '2025-01-07',
      image: '/assets/assets/images/business-shop-sign.webp',
      slug: 'porsche-psm-failure-guide',
    },
    {
      id: 9,
      title: 'VW EPC Light On? How to fix your volkswagen EPC light!',
      excerpt: 'To fix a VW EPC light, scan for trouble codes with an OBD-II scanner. Common causes include throttle body issues, faulty brake light switches, ABS sensor failures, steering angle sensor problems.',
      category: 'Service',
      author: 'Paradise-Performance',
      date: '2025-01-07',
      image: '/assets/assets/images/business-shop-sign.webp',
      slug: 'vw-epc-light-fix-guide',
    },
  ];

  const categories = ['all', 'Service'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const featuredPost = blogPosts[0];
  const regularPosts = blogPosts.slice(1);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-black text-white"
    >
      <Helmet>
        <title>Auto Blog - Car Reviews, Tips & Buying Guides | Paradise-Performance</title>
        <meta name="description" content="Read our latest car reviews, maintenance tips, buying guides, and automotive news. Expert advice to help you make informed vehicle decisions." />
              <link rel="canonical" href="https://paradise-performance.com/blog" />
      </Helmet>

      {/* Hero Section */}
      <PageHero
        eyebrow="Blog & Resources"
        title="Auto Blog & Resources"
        subtitle="Expert automotive advice, reviews, and tips to help you make informed decisions about your next vehicle purchase."
      />

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Search and Filter */}
        <div className="mb-12">
          <div className="flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
            <div className="relative w-full max-w-md flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-12 pr-4 text-white placeholder-gray-500 transition-colors focus:border-red-500/50 focus:outline-none focus:ring-2 focus:ring-red-500/30"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`rounded-xl px-5 py-2.5 text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-red-600 text-white shadow-lg shadow-red-600/25'
                      : 'border border-white/10 bg-white/[0.04] text-gray-300 hover:border-white/20 hover:text-white'
                  }`}
                >
                  {category === 'all' ? 'All' : category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured Post */}
        {selectedCategory === 'all' && !searchTerm && (
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-16"
          >
            <h2 className="mb-8 text-2xl font-bold text-white">Featured Article</h2>
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="group block overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="h-64 overflow-hidden lg:h-auto">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8 md:p-10">
                  <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-gray-400">
                    <span className="rounded-full border border-red-500/30 bg-red-600/15 px-3 py-1 text-xs font-medium uppercase tracking-wider text-red-400">
                      {featuredPost.category}
                    </span>
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiCalendar} className="h-4 w-4" />
                      <span>{formatDate(featuredPost.date)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiUser} className="h-4 w-4" />
                      <span>{featuredPost.author}</span>
                    </div>
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-white transition-colors group-hover:text-red-400 md:text-3xl">{featuredPost.title}</h3>
                  <p className="mb-6 leading-relaxed text-gray-400">{featuredPost.excerpt}</p>
                  <span className="inline-flex items-center space-x-2 font-semibold text-red-500 transition-colors group-hover:text-red-400">
                    <span>Read More</span>
                    <SafeIcon icon={FiArrowRight} className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Blog Posts Grid */}
        <div className="mb-8">
          <h2 className="mb-8 text-2xl font-bold text-white">
            {selectedCategory === 'all' ? 'Latest Articles' : `${selectedCategory} Articles`}
          </h2>
          {filteredPosts.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] py-16 text-center">
              <p className="text-lg text-gray-400">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-red-500/30 hover:bg-white/[0.05]"
                >
                  <Link to={`/blog/${post.slug}`} className="block h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </Link>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="mb-3 flex flex-wrap items-center gap-3 text-sm text-gray-400">
                      <span className="rounded-full border border-red-500/30 bg-red-600/15 px-2.5 py-1 text-xs font-medium uppercase tracking-wider text-red-400">
                        {post.category}
                      </span>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiCalendar} className="h-3 w-3" />
                        <span>{formatDate(post.date)}</span>
                      </div>
                    </div>
                    <h3 className="mb-3 line-clamp-2 text-xl font-semibold text-white transition-colors group-hover:text-red-400">
                      {post.title}
                    </h3>
                    <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-gray-400">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto flex items-center justify-between border-t border-white/[0.06] pt-4">
                      <div className="flex items-center space-x-1 text-sm text-gray-500">
                        <SafeIcon icon={FiUser} className="h-3 w-3" />
                        <span>{post.author}</span>
                      </div>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center space-x-1 text-sm font-semibold text-red-500 transition-colors hover:text-red-400"
                      >
                        <span>Read More</span>
                        <SafeIcon icon={FiArrowRight} className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Newsletter Signup */}
      <section className="bg-neutral-950 py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-red-600/15 via-white/[0.03] to-white/[0.03] p-10 text-center md:p-14">
            <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">Stay Updated</h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-300">
              Subscribe to our newsletter for the latest automotive tips, reviews, and industry news.
            </p>
            <div className="mx-auto flex max-w-md flex-col gap-3 sm:flex-row">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-white placeholder-gray-500 transition-colors focus:border-red-500/50 focus:outline-none focus:ring-2 focus:ring-red-500/30"
              />
              <button className="rounded-xl bg-red-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-red-700">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Blog;